import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Switch, Avatar, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
    getCurrentDoctor,
    updateDoctor,
} from "../../authDoctor/redux/thunks/doctorsThunks";
import useDoctorInformation from "../../ProfileDoctor/hooks/useDoctorInformation";

import AppLayoutDoctor from "../../navigation/Layout/LayoutDoctor";
import { useTour } from "../../appointments/hooks/useTour";
import { settingsTourSteps } from "./settingsTourConfig";
import "./Settings.css";

export default function SettingsDoctor() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const fileRef = useRef(null);
    const [searchParams] = useSearchParams();
    const [shouldStartTour, setShouldStartTour] = useState(false);

    const { user, loading, error } = useSelector((state) => state.doctor);

    // ✅ avatar display + upload state
    const { avatarSrc, isSaving } = useDoctorInformation(user);

    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [birthDate, setBirthDate] = useState("1997-11-21");

    const [appointmentReminders, setAppointmentReminders] = useState(true);
    const [medicationReminders, setMedicationReminders] = useState(true);
    const [messageAlerts, setMessageAlerts] = useState(true);
    const [questionUpdates, setQuestionUpdates] = useState(true);
    const [useLocation, setUseLocation] = useState(true);

    /** Fetch profile */
    useEffect(() => {
        if (!user && !loading) {
            dispatch(getCurrentDoctor());
        }
    }, [dispatch, user, loading]);

    /** Sync form */
    useEffect(() => {
        if (user) {
            setFullName(user.fullName || "");
            setEmail(user.email || "");
            setPhoneNumber(user.phone || "");
            setBirthDate(user.birthDate || "1997-11-21");

            setAppointmentReminders(user.appointmentReminders ?? true);
            setMedicationReminders(user.medicationReminders ?? true);
            setMessageAlerts(user.messageAlerts ?? true);
            setQuestionUpdates(user.questionUpdates ?? true);
            setUseLocation(user.useLocation ?? true);
        }
    }, [user]);

    /** Error popup */
    useEffect(() => {
        if (error) message.error(error);
    }, [error]);

    /** Cleanup preview */
    useEffect(() => {
        return () => {
            if (avatarPreview) URL.revokeObjectURL(avatarPreview);
        };
    }, [avatarPreview]);

    // Guided tour initialization
    useEffect(() => {
        const hasGuide = searchParams.get("guide") === "true";
        const hasTour = searchParams.get("tour") === "settings";
      let timer;

      if ((hasGuide || hasTour) && !shouldStartTour) {
            // Wait for user data to load before starting tour
            if (!loading && user) {
          timer = setTimeout(() => {
                    setShouldStartTour(true);
                }, 1000);
            }
        }

      return () => {
        if (timer) clearTimeout(timer);
      };
    }, [searchParams, loading, user, shouldStartTour]);

    useTour(shouldStartTour, settingsTourSteps);

    /** Pick avatar (no upload yet) */
    const onPickAvatar = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            message.error(t("settings.invalidImage"));
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            message.error(t("settings.imageTooLarge"));
            return;
        }

        setAvatarFile(file);
        const url = URL.createObjectURL(file);
        setAvatarPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
        });

        if (fileRef.current) fileRef.current.value = "";
    };

    /** Save profile */
    const onSave = async () => {
        if (!user?.id) {
            message.error(t("settings.doctorIdMissing"));
            return;
        }

        try {
            const formData = new FormData();
            formData.append("fullName", fullName);
            formData.append("email", email);
            formData.append("phone", phoneNumber);
            formData.append("birthDate", birthDate);

            if (avatarFile) {
                formData.append("avatar", avatarFile);
            }

            await dispatch(updateDoctor({ id: user.id, formData })).unwrap();
            message.success(t("settings.profileUpdatedSuccess"));

            dispatch(getCurrentDoctor());
            setAvatarFile(null);
            setAvatarPreview(null);
        } catch (err) {
            const msg =
                typeof err === "string"
                    ? err
                    : err?.message || t("settings.profileUpdateFailed");
            message.error(msg);
        }
    };

  // ✅ Avatar par défaut comme SubMenu
  const avatarToShow =
    avatarPreview ||
    avatarSrc ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  return (
    <AppLayoutDoctor>
      <div className="settings-page">
        <div className="settings-scrollable-content">
          {/* HEADER */}
          <div className="settings-profile-header">
            <Avatar
              size={64}
              icon={<UserOutlined />}
              src={avatarToShow}
              style={{ objectFit: "cover" }}
            />

            <div className="settings-profile-info">
              <h2>{fullName || t("commonn.loading")}</h2>
              <p>{email || t("commonn.loading")}</p>
            </div>

                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={onPickAvatar}
                            disabled={loading || isSaving}
                        />

                        <Button
                            type="primary"
                            className="edit-picture-btn"
                            onClick={() => fileRef.current?.click()}
                            disabled={loading || isSaving}
                        >
                            {t("settings.editProfilePicture")}
                        </Button>
                    </div>

                    {/* PROFILE INFO */}
                    <div className="settings-card">
                        <div className="settings-card-header">
                            <UserOutlined className="settings-icon" />
                            <div>
                                <h3>{t("settings.profileInfoTitle")}</h3>
                                <p>{t("settings.profileInfoSubtitleDoctor")}</p>
                            </div>
                        </div>

            <div className="settings-card-content">
              <div className="settings-form-grid">
                <div className="settings-field">
                  <label>{t("settings.fullNameLabel")}</label>
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={loading || isSaving}
                  />
                </div>

                {/* 🔒 EMAIL BLOQUÉ */}
                <div className="settings-field">
                  <label>{t("commonn.email")}</label>
                  <Input
                    value={email}
                    disabled
                  />
                </div>

                                <div className="settings-field">
                                    <label>{t("signup.dateOfBirth")}</label>
                                    <input
                                        type="date"
                                        className="settings-date-input"
                                        value={birthDate}
                                        onChange={(e) => setBirthDate(e.target.value)}
                                        disabled={loading || isSaving}
                                    />
                                </div>

                <div className="settings-field">
                  <label>{t("commonn.phone")}</label>
                  <PhoneInput
                    country="tn"
                    enableSearch
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    inputStyle={{ width: "100%" }}
                    disabled={loading || isSaving}
                  />
                </div>
              </div>
            </div>
          </div>

                    {/* NOTIFICATIONS */}
                    <div className="settings-card">
                        <div className="settings-card-header">
                            <span className="settings-icon">🔔</span>
                            <h3>{t("settings.notificationsTitle")}</h3>
                        </div>

            <div className="settings-card-content settings-notification-list">
              {[
                [t("settings.appointmentReminders"), appointmentReminders, setAppointmentReminders],
                [t("settings.medicationReminders"), medicationReminders, setMedicationReminders],
                [t("settings.messageAlerts"), messageAlerts, setMessageAlerts],
                [t("settings.questionUpdates"), questionUpdates, setQuestionUpdates],
                [t("settings.useLocationTitle"), useLocation, setUseLocation],
              ].map(([label, value, setter]) => (
                <div className="settings-notification-item" key={label}>
                  <h4>{label}</h4>
                  <Switch
                    checked={value}
                    onChange={setter}
                    disabled={loading || isSaving}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* SAVE */}
          <div className="settings-footer-fixed">
            <Button
              type="primary"
              size="large"
              onClick={onSave}
              loading={loading || isSaving}
            >
              {loading || isSaving
                ? t("settings.saving")
                : t("settings.saveChanges")}
            </Button>
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
}